import {
	Component, Input, Output, OnInit, OnChanges, SimpleChange, SimpleChanges, OnDestroy,
	ViewChild, EventEmitter, ChangeDetectorRef
} from '@angular/core';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { IdentityService } from 'identity';
import { NavigationService, NetFacadeService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	DisplayDeviceListEntryDto, GetDeviceStateResultDto, DeviceStateDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';


export class DeviceStateListEntry {
	public deviceGuid: string;
	public monitorPolicyGuid: string;
	public monitorSettingsEntryGuid: string;
	public monitorName: string;
	public summary: string;
	public state: string;
	public stateDetails: string;
	public style: string;

	constructor(deviceState?: DeviceStateDto) {
		if (!!deviceState) {
			this.deviceGuid = deviceState.deviceGuid;
			this.monitorPolicyGuid = deviceState.monitorPolicyGuid;
			this.monitorSettingsEntryGuid = deviceState.monitorSettingsEntryGuid;
			this.monitorName = deviceState.monitorPolicyName;
			this.summary = deviceState.monitorSettingsEntrySummary;

			if (!!deviceState.monitorSettingsEntryState) {
				this.state = deviceState.monitorSettingsEntryState;
				if (this.state === "NoData") {
					this.state = "No Data";
				}

				this.style = "monitor-state-" + deviceState.monitorSettingsEntryState.toLowerCase();
			}
			else {
				this.state = "No Data";
				this.style = "monitor-state-nodata";
			}

			if (!!deviceState.monitorSettingsEntryStateDetails) {
				this.stateDetails = deviceState.monitorSettingsEntryStateDetails;
			}
			else {
				this.stateDetails = "";
			}
		}
		else {
			this.deviceGuid = "";
			this.monitorPolicyGuid = "";
			this.monitorSettingsEntryGuid = "";
			this.monitorName = "";
			this.summary = "";
			this.state = "";
			this.stateDetails = "";
			this.style = "";
		}
	}
}

@Component({
	selector: 'device-state',
	templateUrl: './devicestate.component.html',
	styleUrls: ['./devicestate.component.scss']
})
export class DeviceStateComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public visible: boolean;

	@Input()
	public target: DisplayDeviceListEntryDto;

	@Output()
	public visibleChange: EventEmitter<boolean>;

	@Output()
	public onClose: EventEmitter<any>;

	public entries: DeviceStateListEntry[];

	private deviceState: GetDeviceStateResultDto;

	private destroyed: boolean;
	private eventsInterval: number;
	private timerId: any;

	constructor(private monService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private netFacadeService: NetFacadeService,
		private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {

		this.visible = false;
		this.target = null;
		this.visibleChange = new EventEmitter<boolean>();
		this.onClose = new EventEmitter<any>();

		this.entries = [];

		this.deviceState = { entries: [] };

		this.destroyed = false;
		this.eventsInterval = 0;
		this.timerId = undefined;
	}

	public ngOnInit(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.destroyed)
			return;

		if (changes["visible"]) {
			this.stopTimer();
			if (this.visible) {
				this.retrieveDeviceState(true);
			}
			this.startTimer();
		}
	}

	public ngOnDestroy(): void {
		return;
	}

	public get deviceName(): string {
		if (!!this.target) {
			return this.target.name;
		}
		else {
			return "";
		}
	}

	private startTimer(): void {

		this.setEventsInterval();

		if (!this.timerId && this.visible && this.eventsInterval > 0) {
			let timeoutMillis = this.eventsInterval * 1000;
			this.timerId = setInterval(() => {
				this.retrieveDeviceState(false);
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	private setEventsInterval(): void {
		this.eventsInterval = 0;

		let netViewGuid = this.navService.location.netViewGuid;
		let network: NetViewDto = this.netFacadeService.findNetwork(netViewGuid);
		if (network) {
			this.eventsInterval = network.EventsInterval;
		}
		else {
			this.messageService.displayErrorMessage("Could not set events interval");
		}
	}

	public show(): void {
		this.visible = true;
		this.visibleChange.emit(true);

		this.changeDetector.markForCheck();
	}

	public hide(): void {
		this.visible = false;
		this.visibleChange.emit(false);

		this.changeDetector.markForCheck();
	}

	public onCloseClicked(): void {
		this.onClose.emit();
		this.hide();
	}

	private retrieveDeviceState(showBusy: boolean): void {

		if (!!this.target) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			this.monService.getDeviceState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, this.target.deviceGuid, showBusy).subscribe(
					(result: GetDeviceStateResultDto) => {
						this.deviceState = result;
						this.rebuildEntries();
					},
					(error) => {
						this.deviceState = { entries: [] };
						this.rebuildEntries();
						this.messageService.displayErrorMessage("Could not retrieve device state");
					});
		}
	}

	private rebuildEntries(): void {

		this.entries = [];

		if (!!this.deviceState) {
			this.deviceState.entries.forEach((devStateEntry: DeviceStateDto) => {

				let entry = new DeviceStateListEntry(devStateEntry);
				this.entries.push(entry);
			});
		}
	}

	public refreshEntries(): void {
		this.retrieveDeviceState(true);
	}

	public doCustomSort(event): void {
		let comparer = function (a: DeviceStateListEntry, b: DeviceStateListEntry): number {
			let result = 0;

			switch (event.field) {
				default:
					// Compare string fields
					result = a[event.field].toString().localeCompare(b[event.field].toString());
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
